import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { timerAPI } from "../lib/api";
import { useAuth } from "./AuthContext";

const TimerContext = createContext(null);

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error("useTimer must be used within TimerProvider");
  }
  return context;
};

export const TimerProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [activeTimers, setActiveTimers] = useState([]);
  const [tick, setTick] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchActiveTimer = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await timerAPI.getActive();
      setActiveTimers(res.data || []);
    } catch (err) {
      console.error("Error fetching timer:", err);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchActiveTimer();
  }, [fetchActiveTimer]);

  useEffect(() => {
    let interval;
    if (activeTimers.length > 0) {
      interval = setInterval(() => {
        setTick((t) => t + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeTimers.length]);

  const getTimerForClient = (clientId) => {
    return activeTimers.find((t) => t.client_id === clientId) || null;
  };

  const getElapsedForClient = (clientId) => {
    const timer = getTimerForClient(clientId);
    if (!timer) return 0;
    const start = new Date(timer.start_time);
    const now = new Date();
    return Math.floor((now - start) / 1000);
  };

  const formatElapsedSeconds = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const formatElapsedForClient = (clientId) => {
    return formatElapsedSeconds(getElapsedForClient(clientId));
  };

  const startTimer = async (data) => {
    setLoading(true);
    try {
      const res = await timerAPI.start(data);
      setActiveTimers((prev) => [...prev, res.data]);
      return res.data;
    } finally {
      setLoading(false);
    }
  };

  const stopTimer = async (clientId) => {
    setLoading(true);
    try {
      const res = await timerAPI.stop(clientId);
      if (clientId) {
        setActiveTimers((prev) => prev.filter((t) => t.client_id !== clientId));
      } else {
        // Stop the first timer found
        setActiveTimers((prev) => prev.slice(1));
      }
      return res.data;
    } finally {
      setLoading(false);
    }
  };

  const discardTimer = async (clientId) => {
    setLoading(true);
    try {
      await timerAPI.discard(clientId);
      if (clientId) {
        setActiveTimers((prev) => prev.filter((t) => t.client_id !== clientId));
      } else {
        setActiveTimers((prev) => prev.slice(1));
      }
    } finally {
      setLoading(false);
    }
  };

  // Backward-compatible helpers (based on first active timer)
  const activeTimer = activeTimers[0] || null;
  const elapsedSeconds = activeTimer
    ? Math.floor((new Date() - new Date(activeTimer.start_time)) / 1000)
    : 0;
  const formatElapsed = () => formatElapsedSeconds(elapsedSeconds);

  return (
    <TimerContext.Provider
      value={{
        activeTimers,
        activeTimer,
        elapsedSeconds,
        loading,
        tick,
        isRunning: activeTimers.length > 0,
        startTimer,
        stopTimer,
        discardTimer,
        getTimerForClient,
        getElapsedForClient,
        formatElapsedForClient,
        formatElapsed,
        refresh: fetchActiveTimer,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};
